import { useState } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { GREEN, CHARCOAL, BORDER, MUTED, MUTED_FAINT, labelMono, bodyFont } from '../../theme/tokens2';

// ─── Terms sections data ─────────────────────────────────────────────────────
const TERMS_SECTIONS = [
  {
    title: 'Intellectual Property Rights',
    paragraphs: [
      'You agree not to copy, reproduce, modify, extract or use any assets without our written authorisation. However, you may display a reasonable amount of content for the purposes of making references to the Savvy Renovator; in doing you so, you are required to (1) refrain from making any modifications, (2) provide credit, attribution, and backlinks to the Savvy Renovator. Any third party mentioned or represented in any sort on our platform belongs to their respective owners who may or may not be endorsed by, or affiliated with the Savvy Renovator.',
    ],
  },
  {
    title: 'Receiving Matches On The Platform',
    paragraphs: [
      'The matching services on the platform are provided for free to Homeowners and considered to be provided out of goodwill, therefore (1) the Savvy Renovator will not be considered to be involved in any activity conducted beyond matching you with Renovators (2) Although we will make reasonable efforts to ensure that all information is updated and accurate, we will not be liable to you for any damages, disputes, misrepresentation, guarantees, and quality of services provided by any third parties including the Renovators.',
      'You should conduct any necessary checks before proceeding with any transaction with any third parties displayed or recommended by the platform.',
    ],
  },
  {
    title: 'Recommendations Or Information Provided By The Platform',
    paragraphs: [
      'It is important for you and also your own due diligence to assess if any recommendations or information are applicable. If you decide to take up any recommendations or information provided, the Savvy Renovator assumes no liability for any damages (whether direct or speculative) of any kind that may arise out of it.',
    ],
  },
  {
    title: 'Disclaimer Of Liabilities',
    paragraphs: [
      'Your usage of the platform is at your sole risk hence, you agree to not hold the Savvy Renovator or its owners and employees for any possible claims arising out of, or in connection with your use of the platform.',
    ],
  },
];

// ─── Hoverable link helper ────────────────────────────────────────────────────
function NavLink({ to, children, sx = {} }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      component={Link}
      to={to}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        textDecoration: 'none',
        color: hovered ? CHARCOAL : MUTED,
        transition: 'color 0.2s ease',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

// ─── Terms of Use page ────────────────────────────────────────────────────────
export default function TermsOfUse() {
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', ...bodyFont }}>

      {/* ── Top bar ── */}
      <Box sx={{ borderBottom: `1px solid ${BORDER}` }}>
        <Box sx={{
          maxWidth: 1400, mx: 'auto',
          px: { xs: 3, md: 6 },
          height: 64,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          {/* Logo */}
          <Box component={Link} to="/" sx={{textDecoration: 'none'}}>
            <Typography sx={{ ...labelMono, color: CHARCOAL }}>Savvy Renovator</Typography>
          </Box>

          {/* Back link */}
          <NavLink to="/">
            <Typography sx={{ ...labelMono, fontSize: '0.75rem' }}>← Back to Home</Typography>
          </NavLink>
        </Box>
      </Box>

      {/* ── Main content ── */}
      <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 3, md: 6 }, py: { xs: 10, md: 14 } }}>

        {/* Section label */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 6 }}>
          <Typography sx={{ ...labelMono, color: GREEN }}>Disclaimers</Typography>
          <Box sx={{ height: '1px', width: 40, backgroundColor: `${GREEN}4d` }} />
        </Box>

        {/* Page title */}
        <Typography
          component="h1"
          sx={{
            fontSize: 'clamp(2.2rem, 5vw, 3.8rem)',
            fontWeight: 300, lineHeight: 1.06, letterSpacing: '-0.03em',
            color: CHARCOAL, mb: 2, ...bodyFont,
          }}
        >
          Terms of Use
        </Typography>

        {/* Date Last Updated */}
        <Typography sx={{ ...labelMono, color: MUTED_FAINT, mb: 8 }}>
          Last updated: May 2026
        </Typography>

        {/* Body sections */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>

          {/* Intro paras */}
          <Box>
            <Typography sx={{ fontSize: '1rem', color: MUTED, lineHeight: 1.8, ...bodyFont }}>
              To ensure that your experience with us is safe and the decisions are well-informed, please kindly read these terms carefully before using the platform as they represent a binding legal agreement between us. When you access the Website, you are agreeing to abide by this Agreement. If you do not consent to these terms and conditions, kindly refrain from using the Website or any of the provided Information or Services.
            </Typography>
            <Typography sx={{ fontSize: '1rem', color: MUTED, lineHeight: 1.8, ...bodyFont }}>
              The nature of our business may require us to update our terms of use from time to time without notice, therefore, we encourage you to check for any updates or amendments. Thank you for your understanding.
            </Typography>
          </Box>

          {/* Rest of the Body Section */}
          {TERMS_SECTIONS.map((section) => (
            <Box key={section.title}>

              <Typography component="h2" 
              sx={{ fontSize: '1.125rem', fontWeight: 500, color: CHARCOAL, mb: 2, ...bodyFont }}>
                {section.title}
              </Typography>

              {section.paragraphs.map((paragraph, index) => (
                <Typography
                  key={index}
                  sx={{ fontSize: '1rem', color: MUTED, lineHeight: 1.8, ...bodyFont, }}
                >
                  {paragraph}
                </Typography>
              ))}
            </Box>
          ))}

        </Box>
      </Box>

      {/* ── Footer strip ── */}
      <Box sx={{ borderTop: `1px solid ${BORDER}` }}>
        <Box sx={{
          maxWidth: 1400, mx: 'auto',
          px: { xs: 3, md: 6 }, py: 2.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Typography sx={{ ...labelMono, color: MUTED_FAINT }}>
            © {new Date().getFullYear()} Savvy Renovator
          </Typography>
          <NavLink to="/">
            <Typography sx={{ ...labelMono }}>Back to Home</Typography>
          </NavLink>
        </Box>
      </Box>
    </Box>
  );
}